import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import ToastNotification from '../../components/ToastNotification'
import { motion } from 'framer-motion'
import 'primeicons/primeicons.css'

interface Lesson {
  id: number
  title: string
  short_description?: string
  content?: string
  image?: string
  image_url?: string
  video_url?: string
  document_file?: string
  document_file_url?: string
  order: number
  is_active: boolean
}

interface Course {
  id: number
  title: string
  subtitle?: string
  description?: string
  image?: string
  enrollment?: {
    progress: number
    completed_videos: number
    status: string
  }
}

export default function LessonDetailPage() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [completing, setCompleting] = useState(false)

  useEffect(() => {
    const fetchLessonDetail = async () => {
      if (!courseId || !lessonId || !isAuthenticated) return

      try {
        setLoading(true)
        setError(null)
        
        // Fetch course detail to get lessons
        const courseData = await coursesAPI.getCourseDetail(parseInt(courseId))
        const API_BASE_URL = getApiBaseUrlWithoutApi()
        
        // Process course image
        let courseImageUrl = courseData.image || '/images/team-photo-1-BB_Ia2hP.jpg'
        if (courseImageUrl && courseImageUrl.startsWith('/media/')) {
          courseImageUrl = `${API_BASE_URL}${courseImageUrl}`
        } else if (courseImageUrl && !courseImageUrl.startsWith('http') && !courseImageUrl.startsWith('/images/')) {
          courseImageUrl = `${API_BASE_URL}${courseImageUrl}`
        }

        setCourse({
          id: courseData.id,
          title: courseData.title,
          subtitle: courseData.subtitle,
          description: courseData.description,
          image: courseImageUrl
        })

        // Find the specific lesson
        const processedLessons = (courseData.lessons || []).map((l: any) => {
          let docUrl = l.document_file_url
          if (docUrl && docUrl.startsWith('/media/')) {
            docUrl = `${API_BASE_URL}${docUrl}`
          } else if (docUrl && !docUrl.startsWith('http') && !docUrl.startsWith('/images/')) {
            docUrl = `${API_BASE_URL}${docUrl}`
          }

          let lessonImageUrl = l.image_url || l.image
          if (lessonImageUrl && lessonImageUrl.startsWith('/media/')) {
            lessonImageUrl = `${API_BASE_URL}${lessonImageUrl}`
          } else if (lessonImageUrl && !lessonImageUrl.startsWith('http') && !lessonImageUrl.startsWith('/images/')) {
            lessonImageUrl = `${API_BASE_URL}${lessonImageUrl}`
          }

          return {
            ...l,
            document_file_url: docUrl,
            image_url: lessonImageUrl
          }
        }).sort((a: Lesson, b: Lesson) => a.order - b.order)

        const foundLesson = processedLessons.find((l: Lesson) => l.id === parseInt(lessonId))
        
        if (!foundLesson) {
          setError('Bài học không tồn tại')
          return
        }

        setLesson(foundLesson)
        setImageError(false) // Reset image error when lesson changes
      } catch (error: any) {
        if (error.response?.status === 404) {
          setError('Bài học không tồn tại')
        } else {
          setError('Có lỗi xảy ra khi tải bài học')
        }
      } finally {
        setLoading(false)
      }
    }

    if (isAuthenticated) {
      fetchLessonDetail()
    }
  }, [courseId, lessonId, isAuthenticated])

  const handleCompleteLesson = async () => {
    if (!lesson || completing) return
    
    try {
      setCompleting(true)
      const result = await coursesAPI.completeLesson(lesson.id)
      
      // Hiển thị thông báo thành công
      setToast({
        message: result.message || `Đã hoàn thành bài tập hôm nay! Tiến độ: ${result.progress}% (${result.completed_videos}/${result.total_lessons} bài học)`,
        type: 'success'
      })
      
      // Refresh lại course data để cập nhật progress
      if (courseId) {
        try {
          const courseData = await coursesAPI.getCourseDetail(parseInt(courseId))
          const API_BASE_URL = getApiBaseUrlWithoutApi()
          
          let courseImageUrl = courseData.image || '/images/team-photo-1-BB_Ia2hP.jpg'
          if (courseImageUrl && courseImageUrl.startsWith('/media/')) {
            courseImageUrl = `${API_BASE_URL}${courseImageUrl}`
          } else if (courseImageUrl && !courseImageUrl.startsWith('http') && !courseImageUrl.startsWith('/images/')) {
            courseImageUrl = `${API_BASE_URL}${courseImageUrl}`
          }

          setCourse({
            id: courseData.id,
            title: courseData.title,
            subtitle: courseData.subtitle,
            description: courseData.description,
            image: courseImageUrl,
            enrollment: courseData.enrollment
          })
        } catch (refreshError) {
          // Error refreshing course data
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Có lỗi xảy ra khi hoàn thành bài học'
      setToast({
        message: errorMessage,
        type: 'error'
      })
    } finally {
      setCompleting(false)
    }
  }

  const isImageFile = (url: string | undefined): boolean => {
    if (!url) return false
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
    const lowerUrl = url.toLowerCase()
    return imageExtensions.some(ext => lowerUrl.includes(ext))
  }

  if (!isAuthenticated) {
    return (
      <main className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="text-center">
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Vui lòng đăng nhập để xem bài học</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-gold-gradient text-black font-bold rounded-lg"
          >
            Đăng nhập
          </button>
        </div>
      </main>
    )
  }

  if (loading) {
    return (
      <main className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải bài học...</p>
        </div>
      </main>
    )
  }

  if (error || !lesson || !course) {
    return (
      <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-red-500/10' : 'bg-red-100'}`}>
            <i className="pi pi-exclamation-triangle text-5xl text-red-400"></i>
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {error || 'Không tìm thấy bài học'}
          </h2>
          <button
            onClick={() => navigate(`/course/online/${courseId}`)}
            className="px-6 py-2 bg-gold-gradient text-black font-bold rounded-lg"
          >
            Quay lại khóa học
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
      <Header />
      
      {/* Back Button */}
      <div className="container mx-auto px-4 pt-4 pb-2">
        <button
          onClick={() => navigate(`/course/online/${courseId}`)}
          className={`flex items-center gap-2 text-sm md:text-base ${theme === 'dark' ? 'text-gray-300 hover:text-gold-400' : 'text-gray-700 hover:text-gold-600'} transition-colors`}
        >
          <i className="pi pi-arrow-left text-base md:text-lg"></i>
          <span>Quay về</span>
        </button>
      </div>

      {/* Main Content - Lesson Header */}
      <section className={`py-4 md:py-8 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            {/* Left Panel - Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative w-full max-w-sm mx-auto lg:max-w-none overflow-hidden rounded-xl md:rounded-2xl" style={{ aspectRatio: '16/9' }}>
                <img
                  src={lesson.image_url || course.image || '/images/team-photo-1-BB_Ia2hP.jpg'}
                  alt={lesson.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = course.image || '/images/team-photo-1-BB_Ia2hP.jpg'
                  }}
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>
              
              {/* Short Description - Hiển thị dưới hình */}
              {lesson.short_description && (
                <p className={`text-sm md:text-base lg:text-lg leading-relaxed mt-4 md:mt-6 text-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {lesson.short_description}
                </p>
              )}
            </motion.div>

            {/* Right Panel - Description */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col pt-4 lg:pt-0 h-full"
            >
              {/* Lesson Number */}
              <div className={`w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-lg md:rounded-xl flex items-center justify-center font-bold text-2xl md:text-3xl lg:text-4xl mb-4 md:mb-6 flex-shrink-0 ${
                theme === 'dark' ? 'bg-gold-500/95 text-black' : 'bg-gold-400 text-black'
              }`}>
                {lesson.order}
              </div>

              {/* Title */}
              <h1 className={`text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold mb-4 md:mb-6 flex-shrink-0 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {lesson.title}
              </h1>

              {/* Content - Scrollable */}
              {lesson.content && (
                <div 
                  className={`flex-1 overflow-y-auto pr-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}
                  style={{ 
                    maxHeight: 'calc(100vh - 400px)',
                    minHeight: '200px'
                  }}
                >
                  <div 
                    className={`text-sm md:text-base lg:text-lg leading-relaxed ${
                      theme === 'dark' 
                        ? 'bg-black/40 border border-gold-500/20' 
                        : 'bg-white/80 border border-gold-400/20'
                    } rounded-xl p-4 md:p-6`}
                    style={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: '1.8'
                    }}
                  >
                    {lesson.content.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < lesson.content!.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Document Section */}
      {lesson.document_file_url && (
        <section className={`py-6 md:py-8 px-4 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="container mx-auto max-w-7xl">
            <h2 className={`text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 ${
              theme === 'dark' ? 'text-gold-400' : 'text-gold-600'
            }`}>
              TÀI LIỆU HƯỚNG DẪN
            </h2>

            {isImageFile(lesson.document_file_url) && !imageError ? (
              <div className="w-full rounded-xl md:rounded-2xl overflow-hidden">
                <img
                  src={lesson.document_file_url}
                  alt="Tài liệu hướng dẫn"
                  className="w-full h-auto object-contain"
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <div className={`w-full rounded-xl md:rounded-2xl overflow-hidden p-4 md:p-8 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
              }`}>
                <div className="flex flex-col items-center justify-center">
                  <i className="pi pi-file text-4xl md:text-6xl lg:text-8xl mb-3 md:mb-4 text-gray-500"></i>
                  <p className={`text-lg md:text-xl lg:text-2xl font-semibold mb-2 text-center ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Tài liệu sẽ được cập nhật
                  </p>
                  <p className={`text-xs md:text-sm lg:text-base text-center px-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Admin sẽ thêm tài liệu hướng dẫn sớm nhất
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Action Buttons */}
      <section className={`py-6 md:py-8 px-4 pb-8 md:pb-12 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            {/* Xem Video Button */}
            {lesson.video_url ? (
              <a
                href={lesson.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 px-4 md:px-6 py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 transition-all ${
                  theme === 'dark'
                    ? 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-500/50'
                    : 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-400/50'
                } transform hover:scale-105 active:scale-95`}
              >
                <i className="pi pi-play text-lg md:text-xl"></i>
                <span className="whitespace-nowrap">Xem video</span>
              </a>
            ) : (
              <button
                disabled
                className={`flex-1 px-4 md:px-6 py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 transition-all ${
                  theme === 'dark'
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="pi pi-play text-lg md:text-xl"></i>
                <span className="whitespace-nowrap">Xem video</span>
              </button>
            )}

            {/* Hoàn thành bài tập Button */}
            <button
              onClick={handleCompleteLesson}
              disabled={completing}
              className={`flex-1 px-4 md:px-6 py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 border-2 transition-all ${
                completing
                  ? theme === 'dark'
                    ? 'border-gray-600 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : theme === 'dark'
                  ? 'border-gold-400 text-gold-400 hover:bg-gold-400/10'
                  : 'border-gold-500 text-gold-600 hover:bg-gold-50'
              } transform ${completing ? '' : 'hover:scale-105 active:scale-95'}`}
            >
              {completing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                  <span className="text-center">Đang xử lý...</span>
                </>
              ) : (
                <>
                  <i className="pi pi-check-circle text-lg md:text-xl"></i>
                  <span className="text-center">Hoàn thành bài tập hôm nay</span>
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </main>
  )
}

